import * as React from "react";
import { ExternalLink, Globe } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { WebSearchSource } from "@/types/open-chat";
import { getFaviconUrl } from "@/helpers/images";
import { safeExternalUrl } from "@/helpers/safeExternalUrl";

interface Props {
  sources: WebSearchSource[];
  onClick?: (sourceIds: number[]) => void;
}

export const WebCitationButton = ({ sources, onClick }: Props) => {
  const [open, setOpen] = React.useState(false);
  const ids = sources.map(s => s.id);

  const handleClick = () => {
    setOpen(false);
    onClick?.(ids);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          className="mx-0.5 inline-flex items-center justify-center gap-0.5 rounded border border-solid border-midnight-blue-900 bg-[#585451] px-1 py-0.5 text-white opacity-80 transition-all duration-200 hover:bg-midnight-blue-800 hover:opacity-100"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          <Globe size={12} />
          {sources.length > 1 && <span className="text-[10px] leading-none">{sources.length}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0"
        side="top"
        align="start"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <div className="divide-y divide-desert-sand-200">
          {sources.map(source => {
            const safeUrl = safeExternalUrl(source.url);
            const favicon = getFaviconUrl(safeUrl ?? null);
            return (
              <a
                key={source.id}
                href={safeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:bg-ivory-mist-50 flex items-center gap-2.5 px-3 py-2.5 transition-colors"
              >
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ivory-mist-100">
                  {favicon ? (
                    <img src={favicon} alt="" className="h-3.5 w-3.5 rounded-sm" />
                  ) : (
                    <Globe size={12} className="text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-foreground">{source.title || "Source"}</p>
                  {safeUrl && <p className="truncate text-[10px] text-muted-foreground">{safeUrl}</p>}
                </div>
                <ExternalLink size={12} className="shrink-0 text-muted-foreground" />
              </a>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
